import React from 'react';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import { Col, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import InputLabel from 'components/layout/forms/InputLabel';
import NumberWithUnitInputField from 'components/layout/forms/NumberWithUnitInputField';
import translations from 'services/translations/translations';

import getPriceSummary from '../getPriceSummary';
import { getMarginClass } from './getMarginRatioClassName';
import IInstallation from '../IInstallation';
import ISubproduct from '../ISubproduct';

const Summary = ({
  installations,
  productUnit,
  quantity,
  subproducts,
}: {
  installations: IInstallation[];
  productUnit: string;
  quantity: number;
  subproducts: ISubproduct[];
}): JSX.Element => {
  const { t } = useTranslation([translations.offers]);

  const priceSummary = getPriceSummary(installations, subproducts, quantity);

  return (
    <div className="calculation-sum-row">
      <h2>{t(`${translations.calculationWindow}:Calculation`)}</h2>
      {/* Selling price first, then expense and margin: per unit, totals, margin % */}
      <Row>
        <Col className="key-figure">
          <InputLabel>
            {t(`${translations.offers}:Selling price`)} €/
            {t(`${translations.common}:units:${productUnit}`)}
          </InputLabel>
        </Col>
        <Col>
          <InputLabel>
            {t(`${translations.offers}:Expense`)} €/
            {t(`${translations.common}:units:${productUnit}`)}
          </InputLabel>
        </Col>
        <Col className="key-figure">
          <InputLabel>
            {`${t(`${translations.offers}:Contribution margin`)} €`}/
            {t(`${translations.common}:units:${productUnit}`)}
          </InputLabel>
        </Col>
        <Col>
          <InputLabel>{t(`${translations.offers}:Selling price`)}</InputLabel>
        </Col>
        <Col>
          <InputLabel>{t(`${translations.offers}:Expense`)}</InputLabel>
        </Col>
        <Col>
          <InputLabel>
            {`${t(`${translations.offers}:Contribution margin`)} €`}
          </InputLabel>
        </Col>
        <Col className="key-figure">
          <InputLabel>
            {`${t(`${translations.offers}:Contribution margin`)} %`}
          </InputLabel>
        </Col>
      </Row>
      <Row>
        <Col className="key-figure">
          <NumberWithUnitInputField
            disabled
            id="priceForUnit"
            unit={`€/${t(`${translations.common}:units:${productUnit}`)}`}
            value={formatGroupedNumber(priceSummary.sellingPriceForUnit)}
          />
        </Col>
        <Col>
          <NumberWithUnitInputField
            disabled
            id="expense-sum-for-unit"
            unit={`€/${t(`${translations.common}:units:${productUnit}`)}`}
            value={formatGroupedNumber(priceSummary.expenseSumForUnit)}
          />
        </Col>
        <Col className="key-figure">
          <NumberWithUnitInputField
            disabled
            id="contribution-margin-for-unit"
            unit={`€/${t(`${translations.common}:units:${productUnit}`)}`}
            value={formatGroupedNumber(
              priceSummary.shownContributionMarginForUnit
            )}
          />
        </Col>
        <Col>
          <NumberWithUnitInputField
            disabled
            id="summary-price-sum"
            unit="€"
            value={formatGroupedNumber(priceSummary.sellingPrice)}
          />
        </Col>
        <Col>
          <NumberWithUnitInputField
            disabled
            id="summary-expense-sum"
            unit="€"
            value={formatGroupedNumber(priceSummary.expenseSum)}
          />
        </Col>
        <Col>
          <NumberWithUnitInputField
            disabled
            id="summary-contribution-margin-sum"
            unit="€"
            value={formatGroupedNumber(priceSummary.shownContributionMargin)}
          />
        </Col>
        {/* Below 20 % red, above 30 % green, as on the rows */}
        <Col
          className={`key-figure ${
            priceSummary.sellingPrice === 0
              ? ''
              : getMarginClass(priceSummary.contributionMarginRatio)
          }`}
        >
          <NumberWithUnitInputField
            disabled
            id="summary-contribution-margin-ratio-sum"
            unit="%"
            value={formatGroupedNumber(priceSummary.contributionMarginRatio)}
          />
        </Col>
      </Row>
    </div>
  );
};

export default Summary;
